import { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/lib/api-client';
import { useAuthStore } from '@/features/auth/store';
import { useTenantCurrent, useUpdateTenant } from './api';

export function CompanySection() {
  const tenantQuery = useTenantCurrent();
  const update = useUpdateTenant();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'admin';

  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (tenantQuery.data) {
      setName(tenantQuery.data.name);
      setLegalName(tenantQuery.data.legalName ?? '');
      setEmail(tenantQuery.data.email);
      setPhone(tenantQuery.data.phone ?? '');
    }
  }, [tenantQuery.data]);

  const t = tenantQuery.data;
  const dirty =
    t != null &&
    (name.trim() !== t.name ||
      (legalName.trim() || null) !== t.legalName ||
      email.trim().toLowerCase() !== t.email ||
      (phone.trim() || null) !== t.phone);

  const handleSubmit = async () => {
    try {
      await update.mutateAsync({
        name: name.trim(),
        legalName: legalName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
      });
      toast.success('Dados da empresa atualizados');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Building2 className="size-4" />
          Dados da empresa
        </CardTitle>
        <CardDescription>
          {canEdit
            ? 'Mantenha os dados atualizados para emissão de documentos e comunicações.'
            : 'Apenas administradores podem alterar estes dados.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tenantQuery.isLoading || !t ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">Nome fantasia</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={255}
                  disabled={!canEdit}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="legalName">Razão social</Label>
                <Input
                  id="legalName"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  maxLength={255}
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-muted-foreground">CNPJ</Label>
                <Input value={t.cnpj} disabled />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail de contato</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={255}
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+5511988887777"
                maxLength={20}
                disabled={!canEdit}
              />
            </div>

            {canEdit && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSubmit}
                  disabled={!dirty || update.isPending || name.trim().length === 0}
                >
                  {update.isPending ? 'Salvando…' : 'Salvar alterações'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
